import { FieldValueLookupError } from '../../../common/errors';
import type {
	MultiSelectPublicSetValue,
	PublicGetValue,
} from '../../../common/fields/multi-select';
import type { OptionsVisibility } from '../../../common/types/options-visibility';
import { applyOptionsVisibilityRule } from '../../../common/utils/apply-options-visibility-rule';
import { getAvailableOptionsVisibilityRule } from '../../../common/utils/get-available-options-visibility-rule';
import { getValueLookupValuesDiff } from '../../../common/utils/get-value-lookup-values-diff';
import { isNotEmptyString } from '../../../common/utils/is-not-empty-string';
import { multiValueLookup } from '../../../common/utils/multi-value-lookup';

export type InternalValue = {
	label: string;
	value: string;
};

export type MultiSelectLookupValue = {
	id: string;
	value: string;
};

export type MultiSelectPublicGetValue = PublicGetValue[];

export type MultiSelectInternalValue = InternalValue[];

export const transformMultiSelectSetOptionsVisibility = (
	optionsVisibility: OptionsVisibility,
	lookupValues: MultiSelectPublicGetValue,
): MultiSelectLookupValue[] => applyOptionsVisibilityRule(optionsVisibility, lookupValues, 'id');

export const transformMultiSelectGetOptionsVisibility = (
	optionsVisibility: OptionsVisibility,
	lookupValues: MultiSelectPublicGetValue,
): OptionsVisibility => getAvailableOptionsVisibilityRule(optionsVisibility, lookupValues, 'id');

export const validateMultiSelectSetValue = (value: unknown): value is MultiSelectPublicSetValue =>
	Array.isArray(value) && value.every(isNotEmptyString);

export const multiSelectSetValueTransformer = (
	values: MultiSelectPublicSetValue,
	lookupValues?: MultiSelectLookupValue[],
): MultiSelectInternalValue => {
	if (!values.length) return [];

	const filteredValues = multiValueLookup<PublicGetValue>(values, lookupValues, 'id');

	if (filteredValues.length !== values.length) {
		const notFoundValues = getValueLookupValuesDiff(values, filteredValues, 'id');

		throw new FieldValueLookupError(notFoundValues.join());
	}

	return filteredValues.map((val) => ({ label: val.value, value: val.id }));
};

export const multiSelectGetValueTransformer = (
	internalValue: MultiSelectInternalValue,
): MultiSelectPublicGetValue => {
	if (!internalValue) return [];

	return internalValue.map(({ value, label }) => ({
		id: value,
		value: label,
	}));
};
